import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { paymentService } from '../services/paymentService';
import { addonService } from '../services/addonService';
import { triggerGlobalSubscriptionRefresh, useSubscriptionContext } from '../contexts/SubscriptionContext';

interface InlinePaymentFormProps {
  planType: 'essential' | 'gold' | 'premium';
  planName: string;
  planPrice: number;
  onSuccess: (subscription: any) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  isAddon?: boolean;
  addonType?: string;
  bundleAddonType?: string;
}

const PaymentForm: React.FC<{
  planType: 'essential' | 'gold' | 'premium';
  planName: string;
  planPrice: number;
  onSuccess: (subscription: any) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  isAddon?: boolean;
  addonType?: string;
  bundleAddonType?: string;
}> = ({ planType, planName, planPrice, onSuccess, onError, onCancel, isAddon, addonType, bundleAddonType }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { subscription } = useSubscriptionContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Handle addon purchase
      if (isAddon && addonType && subscription && subscription.id) {
        // Collect payment method for addon purchase
        const { error: setupError, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.origin,
          },
          redirect: 'if_required',
        });

        if (setupError) {
          throw new Error(setupError.message || 'Payment method setup failed');
        }

        if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
          // Purchase addon with the saved payment method
          // If subscription has Stripe subscription, payment method will be used for addon
          // If no Stripe subscription, it will be created with base plan + addon
          await addonService.purchaseAddon(
            subscription.id,
            addonType,
            setupIntent.payment_method as string
          );

          // Trigger subscription refresh to get updated addons
          triggerGlobalSubscriptionRefresh();
          onSuccess(subscription);
          return;
        } else {
          throw new Error('Payment method setup was not completed');
        }
      }

      // Handle subscription upgrade/purchase
      if (subscription && subscription.id) {
        // User has existing subscription - upgrade it directly
        await paymentService.changeSubscriptionPlan(subscription.id, planType);
        triggerGlobalSubscriptionRefresh();
        onSuccess(subscription);
        return;
      }

      // User doesn't have subscription - proceed with payment method setup
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin,
        },
        redirect: 'if_required',
      });

      if (setupError) {
        throw new Error(setupError.message || 'Payment method setup failed');
      }

      if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
        const subscription = await paymentService.createSubscriptionWithPaymentMethod(
          planType,
          setupIntent.payment_method as string,
          bundleAddonType
        );

        triggerGlobalSubscriptionRefresh();
        onSuccess(subscription);
      } else {
        throw new Error('Payment method setup was not completed');
      }
    } catch (err) {
      let errorMessage = 'Payment failed';

      if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('canceled subscription')) {
          errorMessage = 'Your subscription was canceled. Please contact support to reactivate your account.';
        } else if (err.message.includes('Invalid plan type')) {
          errorMessage = 'The selected plan is not available. Please try again.';
        } else if (err.message.includes('Subscription not found')) {
          errorMessage = 'Your subscription could not be found. Please refresh the page and try again.';
        } else if (err.message.includes('Failed to change subscription plan')) {
          errorMessage = 'Unable to upgrade your subscription. Please try again or contact support.';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      onError(errorMessage);
      console.error('Payment error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-blue-900">{planName}</h3>
        <p className="text-blue-700">${planPrice}/year</p>
        {isAddon && subscription && subscription.id && (
          <p className="text-sm text-blue-600 mt-1">
            Adding to your {subscription.plan_type} subscription
          </p>
        )}
        {!isAddon && subscription && subscription.id && (
          <p className="text-sm text-blue-600 mt-1">
            Upgrading from {subscription.plan_type} plan
          </p>
        )}
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Payment Information
        </label>
        <div className="p-4 bg-white rounded-lg border border-gray-300">
          <PaymentElement
            options={{
              layout: 'tabs',
              defaultValues: {
                billingDetails: {
                  name: '',
                  email: '',
                }
              }
            }}
          />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 rounded-md border border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <div className="flex justify-center items-center space-x-2">
              <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
              <span>Processing...</span>
            </div>
          ) : (
            isAddon
              ? `Purchase ${planName}`
              : subscription && subscription.id
              ? `Upgrade to ${planName}`
              : `Subscribe to ${planName}`
          )}
        </button>
      </div>

      <p className="text-xs text-center text-gray-500">
        Your payment information is secure and encrypted. You can cancel your subscription at any time.
      </p>
    </form>
  );
};

const InlinePaymentForm: React.FC<InlinePaymentFormProps> = ({
  planType,
  planName,
  planPrice,
  onSuccess,
  onError,
  onCancel,
  isAddon,
  addonType,
  bundleAddonType
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        const { client_secret } = await paymentService.createSetupIntent();
        setClientSecret(client_secret);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment';
        setError(errorMessage);
        onError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [onError]);

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <div className="mx-auto mb-4 w-8 h-8 rounded-full border-4 border-blue-600 animate-spin border-t-transparent"></div>
        <p className="text-gray-600">Initializing payment form...</p>
      </div>
    );
  }

  if (error || !clientSecret) {
    return (
      <div className="py-8 text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Setup Failed</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <Elements stripe={loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!)} options={{ clientSecret }}>
      <PaymentForm
        planType={planType}
        planName={planName}
        planPrice={planPrice}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
        isAddon={isAddon}
        addonType={addonType}
        bundleAddonType={bundleAddonType}
      />
    </Elements>
  );
};

export default InlinePaymentForm;
